// @givmo/app-charity — the Admin Console's PAGES, declared as data (CPA-WEB40 · shell contract v3).
//
// ══ THREE PAGES, ONE DECLARATION ═══════════════════════════════════════════════════════════════
//
// The Admin Console is `Apps` · `Team access` · `Activity` (§9-A A3; D-CPO-31 OQ4: three rows, not
// four — the pending access requests are a section of Team access, beneath the roster and the
// invites, never a page of their own). Every consumer of that fact reads THIS array: the sidebar
// family (`orgPageNav`), the routes,
// and the capability each page gates on. A page cannot reach the shell without going through it.
//
// ══ EACH PAGE NAMES ITS OWN CAPABILITY — THE BUILD LAW ════════════════════════════════════════
//
// `capability` is the class-independent name the ONE standing read serves (`write_settings` ·
// `write_member_access` · `read_events`): the page's route gate reads it (`require-capability.tsx`),
// the sidebar offers only the rows whose capability is held, and the Admin Console tile renders
// when any of them is. Never a tier, never a class string — a Global Admin holds all three by
// tier; a custom permission set may hold one.
//
// ══ WHY A REGISTRY WITH A REVEAL LAW ═══════════════════════════════════════════════════════════
//
// A FOURTH page is already named and must not be able to arrive by someone editing the shell:
// **Security** is a reserved page whose org-side capability does not exist yet — and on the charity
// class it is STRUCTURALLY unrevealed, because no charity-class auth-policy surface exists at all.
// The law it arrives under — a page reveals only on a SETTLED positive capability signal, never on a
// client flag — is only enforceable if there is somewhere to enforce it. This module is that
// somewhere, the same shape as `apps/company/src/features/org/org-pages.ts`, and `org-pages.test.ts`
// is the enforcement.
//
// ⚠ SECURITY IS NOT IN THE ARRAY BELOW, and its absence is the deliverable rather than an omission.

import type { LucideLikeIcon } from '@givmo/design-system';
import {
  CAPABILITY_READ_EVENTS,
  CAPABILITY_WRITE_MEMBER_ACCESS,
  CAPABILITY_WRITE_SETTINGS,
  TEAM_COPY,
  holdsCapability,
  type PageNav,
} from '@givmo/org-console';
import { Boxes, History, Users } from 'lucide-react';
import { ACTIVITY_ROUTE, ORG_ROUTE, TEAM_ACCESS_ROUTE } from '../../console/console-routes';
import { ADMIN_CONSOLE_LABEL } from '../../console/module-table';

export { ADMIN_CONSOLE_LABEL };

export interface OrgPageDefinition {
  /** Stable id — part of the testid contract and of the reveal map's keys. Never rename a shipped one. */
  id: string;
  /** The nav row's label, which is also the page-only breadcrumb's word. */
  label: string;
  /** The route it lives at. */
  to: string;
  icon: LucideLikeIcon;
  /** Sort key, ascending. Spaced by decades so a later page can slot between. */
  order: number;
  /** The capability this page gates on — the standing read's snake_case value. */
  capability: string;
  /**
   * This page's capability is not served everywhere, so it renders ONLY on a settled positive signal.
   *
   * Omitted means the page's operations are part of the console itself — served wherever the console
   * is, with the panels' own not-available notices answering the environment where it is dark. Set it
   * and the page is invisible until {@link resolveOrgPages} is handed `true` for this id.
   */
  requiresReveal?: true;
}

/**
 * The sidebar row's `data-testid` for a page id (`menu-org-apps`). The DERIVATION is the contract —
 * the row's id is `org-<page id>` and the package renders `menu-<row id>`, so a page cannot ship
 * without one and the organization's rows can never collide with a class row or a module's.
 */
export function orgNavTestId(id: string): string {
  return `menu-${orgNavRowId(id)}`;
}

/** The sidebar row id for a page id — `org-<id>`. */
export function orgNavRowId(id: string): string {
  return `org-${id}`;
}

/**
 * What each reveal-gated page's probe has SETTLED at. `undefined` means "not asked yet / not settled",
 * and it hides — the same reading as `false`, deliberately: only a positive answer reveals.
 */
export type OrgPageReveals = Readonly<Record<string, boolean | undefined>>;

/** The Admin Console's pages, in the order they are declared. */
export const ORG_PAGES: readonly OrgPageDefinition[] = [
  {
    id: 'apps',
    label: 'Apps',
    to: ORG_ROUTE,
    icon: Boxes,
    order: 10,
    capability: CAPABILITY_WRITE_SETTINGS,
  },
  {
    id: 'team-access',
    // The one page's one name (OM-100, D-CPO-78): every app's Team page and this one.
    label: TEAM_COPY.adminTeam,
    to: TEAM_ACCESS_ROUTE,
    icon: Users,
    order: 20,
    capability: CAPABILITY_WRITE_MEMBER_ACCESS,
  },
  {
    id: 'activity',
    label: 'Activity',
    to: ACTIVITY_ROUTE,
    icon: History,
    order: 30,
    capability: CAPABILITY_READ_EVENTS,
  },
];

/**
 * The pages that render for this visit under the reveal law.
 *
 * `pages` is injectable so the reveal law can be exercised against a declaration the estate does not
 * ship yet — which is the only way to test a law whose first subject has not arrived.
 */
export function resolveOrgPages(
  reveals: OrgPageReveals = {},
  pages: readonly OrgPageDefinition[] = ORG_PAGES,
): OrgPageDefinition[] {
  return pages
    .filter((page) => page.requiresReveal !== true || reveals[page.id] === true)
    .slice()
    .sort((a, b) => a.order - b.order);
}

/**
 * The pages THIS person may open: the reveal law, then the capability each page declares.
 *
 * ⚠ `null` ADMITS EVERY PAGE. An unreported capability list is a backend predating the field, a
 * dark console or a read that errored — the permissive posture every v3 rule takes on `null`,
 * mirrored here so the family does not vanish from a real Global Admin's sidebar during a rollout
 * window. The Admin Console TILE takes the opposite direction (it never draws on a guess) and the
 * two are deliberately different: a tile is an offer, a row on a page you already opened is a name
 * for where you are.
 */
export function orgPagesFor(
  capabilities: readonly string[] | null,
  reveals: OrgPageReveals = {},
  pages: readonly OrgPageDefinition[] = ORG_PAGES,
): OrgPageDefinition[] {
  return resolveOrgPages(reveals, pages).filter(
    (page) => capabilities === null || holdsCapability(capabilities, page.capability),
  );
}

/**
 * The Admin Console family as the sidebar rule reads it (`declarations.pages`): one section under
 * the family's own name, a row per page this person may open, matched by PREFIX so every `/org/…`
 * page wears it. The rows require nothing further — the capability filter above is the gate, and
 * the page's own route gate re-checks it.
 *
 * No Organization section here: the family stands alone (walkthrough decision, 2026-09-03).
 */
export function orgPageNav(
  capabilities: readonly string[] | null,
  reveals: OrgPageReveals = {},
): PageNav<LucideLikeIcon> {
  return {
    path: ORG_ROUTE,
    prefix: true,
    title: ADMIN_CONSOLE_LABEL,
    // The top bar's word on every page of the family (Org Machinery OM-26).
    app: ADMIN_CONSOLE_LABEL,
    includeOrganizationSection: false,
    sections: [
      {
        id: 'admin-console',
        label: ADMIN_CONSOLE_LABEL,
        rows: orgPagesFor(capabilities, reveals).map((page) => ({
          id: orgNavRowId(page.id),
          label: page.label,
          icon: page.icon,
          destination: { kind: 'page', path: page.to },
          requires: '',
        })),
      },
    ],
  };
}
